/**
 * Admin → Payroll PDF Diagnostic Upload.
 *
 * A secure holding area for a payroll PDF that failed to parse, so it can be
 * examined in a later diagnostic session. Admin-only (the whole /admin area is).
 *
 * The file name is shown as PLAIN TEXT, not a link — the app has no route that
 * serves the bytes back, by design. A lodged PDF is reachable only from blob
 * storage directly, so a signed-in user cannot read it through the app. Upload,
 * see who lodged what and when, and delete when it is no longer needed.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getDiagnostics, uploadDiagnostic, deleteDiagnostic, type DiagnosticFile } from "../../payroll/api";

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

const humanSize = (n: number): string =>
  n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

const fmtWhen = (iso: string): string => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${p(d.getDate())} ${months[d.getMonth()]} ${d.getFullYear()}, ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function DiagnosticUploadTab() {
  const [files, setFiles] = useState<DiagnosticFile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setFiles(await getDiagnostics());
    } catch (e) {
      setError(msg(e));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) {
      setError("Only a PDF is accepted.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await uploadDiagnostic(file);
      setNotice(`${file.name} lodged.`);
      await load();
    } catch (e) {
      setError(msg(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(f: DiagnosticFile) {
    if (!confirm(`Delete "${f.fileName}" from the diagnostic holding area? This removes the stored copy.`)) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await deleteDiagnostic(f.id);
      setNotice(`${f.fileName} deleted.`);
      await load();
    } catch (e) {
      setError(msg(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <header className="panel__head">
        <div>
          <h2>Payroll PDF Diagnostic Upload</h2>
          <p className="panel__sub">
            A secure holding area for a payroll PDF that would not parse, so it can be looked at when
            the fault is investigated. The file is stored where the investigation can reach it — it is
            <strong> not downloadable from here</strong>, so the name below is shown as plain text, not
            a link. Delete a file once it is no longer needed.
          </p>
        </div>
        <div className="tm-headactions">
          <button className="btn" onClick={() => void load()} disabled={busy}>
            Refresh
          </button>
          <button className="btn btn--primary" onClick={() => inputRef.current?.click()} disabled={busy}>
            {busy ? "Working…" : "Upload PDF"}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            style={{ display: "none" }}
            onChange={onPick}
          />
        </div>
      </header>

      {error && <div className="alert alert--error">{error}</div>}
      {notice && !error && <div className="alert alert--ok">{notice}</div>}

      {files === null ? (
        <p className="muted">Loading…</p>
      ) : files.length === 0 ? (
        <div className="empty">
          <p>No PDFs held.</p>
          <p className="muted">Use “Upload PDF” to lodge a payroll report for diagnosis.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>File</th>
                <th>Size</th>
                <th>Uploaded by</th>
                <th>Uploaded</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.id}>
                  <td>
                    {/* Deliberately a span, not an anchor — the file is not
                        downloadable through the app. Styled to look like a link
                        so it reads as a file, but it does nothing when clicked. */}
                    <span
                      title="Stored securely — not downloadable from here"
                      style={{
                        color: "var(--nymr-green-light)",
                        textDecoration: "underline",
                        textDecorationStyle: "dotted",
                        cursor: "default",
                      }}
                    >
                      📄 {f.fileName}
                    </span>
                  </td>
                  <td className="mono">{humanSize(f.sizeBytes)}</td>
                  <td>{f.uploadedBy}</td>
                  <td>{fmtWhen(f.uploadedUtc)}</td>
                  <td className="row-actions">
                    <button className="btn btn--small tm-danger" onClick={() => remove(f)} disabled={busy}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
